import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { graphQLClient } from 'graphql/graphQLClient';
import { REPORT_SALES_PAGE_SIZE, toReportEndDate, toReportStartDate } from 'utils/reportSalesUtils';

export interface ReportSalesItem {
  reason: string;
  order_id: string;
  total_paid_amount: number;
  date_created: string;
  seller_custom_field: string | null;
  sale_fee: number;
  statusOrder: string;
  statusShipment: string | null;
  amazon_weight: number
  amazon_price: number
  cost: number;
  total: number;
}

export interface ReportSalesTotals {
  total_paid_amount: number;
  sale_fee: number;
  cost: number;
  total: number;
  total_usa?: number;
  total_ganancia?: number;
}

export interface ReportSalesResult {
  sales: ReportSalesItem[];
  offset: number;
  limit: number;
  total: number;
}

export interface ReportSalesTotalsResult {
  total: number;
  totals: ReportSalesTotals;
}

export interface ReportSalesResponse {
  reportSales: ReportSalesResult;
}

export interface ReportSalesTotalsResponse {
  reportSalesTotals: ReportSalesTotalsResult;
}

export interface ReportSalesQueryParams {
  startDate: string;
  endDate: string;
  page: number;
}

export const REPORT_SALES_QUERY_KEY = 'reportSales';

export const REPORT_SALES_QUERY = `
query ReportSales($startDate: String!, $endDate: String!, $offset: Int!, $limit: Int!) {
  reportSales(startDate: $startDate, endDate: $endDate, offset: $offset, limit: $limit) {
    sales {
      reason
      order_id
      total_paid_amount
      date_created
      seller_custom_field
      sale_fee
      statusOrder
      statusShipment
      amazon_weight
      amazon_price
      cost
      total
    }
    offset
    limit
    total
  }
}
`;

export const REPORT_SALES_TOTALS_QUERY = `
query ReportSalesTotals($startDate: String!, $endDate: String!) {
  reportSalesTotals(startDate: $startDate, endDate: $endDate) {
    total
    totals {
      total_paid_amount
      sale_fee
      cost
      total
      total_usa
      total_ganancia
    }
  }
}
`;

export const getReportSalesErrorMessage = (result: {
  error?: {
    graphQLErrors?: Array<{ message?: string }>;
    fetchError?: { message?: string };
    httpError?: unknown;
  };
}) =>
  result.error?.graphQLErrors?.[0]?.message ||
  result.error?.fetchError?.message ||
  (result.error?.httpError ? 'Error al consultar el reporte de ventas' : null);

export async function fetchReportSales({ startDate, endDate, page }: ReportSalesQueryParams) {
  const result = await graphQLClient.request<ReportSalesResponse>({
    query: REPORT_SALES_QUERY,
    variables: {
      startDate: toReportStartDate(startDate),
      endDate: toReportEndDate(endDate),
      offset: (page - 1) * REPORT_SALES_PAGE_SIZE,
      limit: REPORT_SALES_PAGE_SIZE,
    },
  });

  const errorMessage = getReportSalesErrorMessage(result);

  if (errorMessage) {
    throw new Error(errorMessage);
  }

  if (!result.data?.reportSales) {
    throw new Error('No se recibieron datos del reporte de ventas');
  }

  return result.data.reportSales;
}

export function useReportSales(params: ReportSalesQueryParams | null) {
  return useQuery({
    queryKey: [REPORT_SALES_QUERY_KEY, params?.startDate, params?.endDate, params?.page],
    queryFn: () => fetchReportSales(params!),
    enabled: Boolean(params?.startDate && params?.endDate),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}
